/**
 * Generate a UUID v4. Prefers crypto.randomUUID (modern browsers + secure
 * contexts), falls back to a hand-rolled RFC 4122 v4 backed by
 * crypto.getRandomValues for older Safari / iOS where randomUUID is missing
 * but WebCrypto is available, and finally drops to a non-RFC timestamp +
 * Math.random fingerprint only when WebCrypto is entirely unavailable
 * (insecure context, ancient browser). The shared event_id is used by Meta
 * Pixel/CAPI dedup and Salesforce upserts; collisions are costly so the
 * non-cryptographic fallback is reserved for the genuinely-no-better case.
 */
export function uuidv4(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      return rfc4122v4();
    }
  } catch {
    /* fall through */
  }
  return Date.now() + '-' + Math.random().toString(36).slice(2, 11);
}

function rfc4122v4(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Per RFC 4122 sec 4.4: set version (4) and variant (10xx).
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) {
    hex.push(((bytes[i] as number) + 0x100).toString(16).slice(1));
  }
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}
